import { Checkbox } from '@/components/ui/checkbox';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';
import { cn } from '@/lib/utils';

import { type Referral, effectiveOwner, timeAgoLabel } from '@/features/referrals/referrals-store';

import { customerHolderAge } from '../customer-derived';
import { customerEligibility } from '../customer-eligibility';
import { HoldingsChips } from './HoldingsChips';
import { SignalDots } from './SignalDots';

interface LedgerRowProps {
  /**
   * SCRUM-956 — render the BRANCH/REGION cells. Supplied by `CustomerLedger` from the
   * SAME boolean that composes the header columns, so the two cannot disagree.
   */
  showGeo?: boolean;
  customer: Customer;
  selected: boolean;
  /** SCRUM-389 tellerMode: false hides the selection checkbox cell. */
  selectable?: boolean;
  /** True when this row's customer is the one open in the detail drawer. */
  active: boolean;
  onToggleSelect: (id: string) => void;
  onOpen: (id: string) => void;
  /** When set, render the best-match cell against these products. */
  matchProducts?: Product[];
  /** Active (non-denied) referral for this customer — SCRUM-335 treatment. */
  referral?: Referral | null;
}

/**
 * A single customer row in the ledger (SCRUM-206). Clicking the row body opens
 * the detail drawer (Subtask C); clicking the checkbox toggles selection
 * without opening the drawer.
 */
export function LedgerRow({
  selectable = true,
  customer: c,
  selected,
  active,
  onToggleSelect,
  onOpen,
  matchProducts,
  referral = null,
  showGeo = false,
}: LedgerRowProps) {
  const holderAge = customerHolderAge(c);
  const isBusiness = c.accountType === 'Business';
  const lastCampaignFresh = c.lastCampaign != null && c.lastCampaign.daysAgo <= 14;

  // Best match across the active product filter (Ryan's criteria strip).
  let bestMatch: { met: number; total: number; full: boolean } | null = null;
  if (matchProducts && matchProducts.length > 0) {
    for (const p of matchProducts) {
      const e = customerEligibility(c, p);
      const ratio = e.total ? e.met / e.total : 0;
      const bestRatio = bestMatch ? bestMatch.met / bestMatch.total : -1;
      if (ratio > bestRatio) {
        bestMatch = { met: e.met, total: e.total, full: e.met === e.total };
      }
    }
  }

  return (
    <tr
      onClick={() => {
        onOpen(c.id);
      }}
      // Ryan bankIQ 4 (.ledger-row.selected/.inspecting): selected AND inspecting
      // share the SAME gold-dim tint (var(--cyan-dim) = bg-brand-dim) and their
      // bottom border takes the gold-tinted accent (var(--border-accent)). The
      // bottom border lives on the <tr> (Tailwind border-b), so the accent is a
      // border-color override on the <tr> — not on the <td> (which carries no
      // border width). Hover is intentionally NOT applied to an active/selected
      // row, otherwise hover:bg-bg-3 would mask the tint while the pointer rests
      // on the row. The only distinguisher for the inspecting (drawer-open) row is
      // a 2px gold inset bar on the first cell (see the checkbox <td> below).
      className={cn(
        'cursor-pointer border-b last:border-0',
        active || selected
          ? 'bg-brand-dim border-border-accent'
          : referral
            ? 'border-border-1 [background:color-mix(in_srgb,var(--cyan)_5%,transparent)] hover:[background:color-mix(in_srgb,var(--cyan)_9%,transparent)]'
            : 'border-border-1 hover:bg-bg-3',
      )}
    >
      {selectable && (
        <td
          // 2px gold inset bar on the inspecting row's first cell — draws the left
          // edge without layout shift (Ryan's .ledger-row.inspecting td:first-child).
          className={cn(
            'px-3 py-2',
            active
              ? 'shadow-[inset_2px_0_0_0_var(--cyan)]'
              : referral
                ? 'shadow-[inset_2px_0_0_0_color-mix(in_srgb,var(--cyan)_55%,transparent)]'
                : undefined,
          )}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={() => {
              onToggleSelect(c.id);
            }}
            aria-label={`Select ${c.name}`}
          />
        </td>
      )}
      <td className="px-3 py-2">
        <span className="text-text-0 font-medium">{c.name}</span>
        {referral && (
          <span className="text-brand-primary mt-0.5 flex items-center gap-1 text-[0.6875rem] font-medium">
            <svg
              viewBox="0 0 12 12"
              width="10"
              height="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="flex-none"
            >
              <circle cx="3" cy="6" r="1.6" />
              <circle cx="9" cy="3" r="1.6" />
              <circle cx="9" cy="9" r="1.6" />
              <path d="M4.4 5.3l3.2-1.6M4.4 6.7l3.2 1.6" />
            </svg>
            <span className="max-w-[220px] truncate whitespace-nowrap">
              {referral.status === 'campaign_sent'
                ? `In progress · ${effectiveOwner(referral).name}`
                : `Referred by ${referral.tellerName} · ${timeAgoLabel(referral.submittedDaysAgo)}`}
            </span>
          </span>
        )}
      </td>
      <td className="text-text-2 px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: isBusiness ? 'var(--cyan)' : 'var(--text-3)' }}
          />
          {c.accountType}
        </span>
      </td>
      <td className="text-text-1 px-3 py-2 text-right tabular-nums">{holderAge ?? '—'}</td>
      <td className="text-text-1 px-3 py-2 text-right tabular-nums">{c.ageMo} mo</td>
      {/* SCRUM-977 — geography. Position is load-bearing and MUST match the header
          composition in CustomerLedger, which splices GEO_COLUMNS in after TENURE
          (`age`) to honour Ryan's order: CUSTOMER · TYPE · HOLDER AGE · TENURE ·
          BRANCH · REGION · BALANCE · HOLDINGS · SIGNALS · [MATCH] · LAST. These
          cells therefore sit AFTER tenure and BEFORE balance. LedgerRow is
          hand-coded (not COLUMNS-driven), so a mismatch here shifts every
          following cell one column and de-aligns the whole table — the
          alignment test asserts label-for-label correspondence, not just counts. */}
      {showGeo && (
        <>
          <td className="text-text-2 px-3 py-2">{c.branch || '—'}</td>
          <td className="text-text-2 px-3 py-2">{c.region || '—'}</td>
        </>
      )}
      <td className="text-text-1 px-3 py-2 text-right font-mono tabular-nums">
        ${c.balance.toLocaleString('en-US')}
      </td>
      <td className="px-3 py-2">
        <HoldingsChips holdings={c.holdings} />
      </td>
      <td className="px-3 py-2">
        <SignalDots insights={c.insights} />
      </td>
      {bestMatch && (
        <td className="px-3 py-2">
          <span className="flex items-center gap-2">
            <span className="flex gap-0.5">
              {Array.from({ length: bestMatch.total }, (_, i) => (
                <span
                  key={i}
                  className="h-3 w-[7px] rounded-[1px]"
                  style={{
                    backgroundColor: i < bestMatch.met ? 'var(--cyan)' : 'var(--bg-3)',
                  }}
                />
              ))}
            </span>
            <span className="text-text-2 font-mono text-xs tabular-nums">
              {bestMatch.met}/{bestMatch.total}
            </span>
          </span>
        </td>
      )}
      <td className="text-text-2 px-3 py-2 text-right tabular-nums">
        {c.lastCampaign ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: lastCampaignFresh ? 'var(--cyan)' : 'var(--text-3)' }}
            />
            {c.lastCampaign.daysAgo}d
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}
